import { useEffect, useState, useMemo, useCallback } from "react";
import { api } from "@/lib/api";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import EventFormDialog from "@/components/EventFormDialog";
import { CalendarDays, ChevronLeft, ChevronRight, Plus, Loader2 } from "lucide-react";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export default function Calendar() {
  const [calendars, setCalendars] = useState([]);
  const [mailbox, setMailbox] = useState("");
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [defaultDate, setDefaultDate] = useState(null);

  useEffect(() => {
    api.get("/calendar/calendars").then((r) => {
      setCalendars(r.data);
      if (r.data[0]) setMailbox(r.data[0].id);
    }).catch(() => {});
  }, []);

  const days = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const startGrid = new Date(first);
    startGrid.setDate(first.getDate() - first.getDay());
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(startGrid);
      d.setDate(startGrid.getDate() + i);
      return d;
    });
  }, [cursor]);

  const load = useCallback(() => {
    if (!mailbox || days.length === 0) return;
    setLoading(true); setError("");
    const start = `${ymd(days[0])}T00:00:00`;
    const end = `${ymd(days[days.length - 1])}T23:59:59`;
    api.get("/calendar/events", { params: { start, end, mailbox } })
      .then((r) => setEvents(r.data.events || []))
      .catch((err) => { setEvents([]); setError(err.response?.status === 502 ? "This calendar can't be accessed (it may not be shared with the app yet)." : "Could not load events."); })
      .finally(() => setLoading(false));
  }, [mailbox, days]);

  useEffect(() => { load(); }, [load]);

  const eventsByDay = useMemo(() => {
    const map = {};
    for (const ev of events) {
      const key = (ev.start?.dateTime || "").slice(0, 10);
      (map[key] = map[key] || []).push(ev);
    }
    return map;
  }, [events]);

  const openNew = (dateStr) => { setEditing(null); setDefaultDate(dateStr); setDialogOpen(true); };
  const openEdit = (ev) => { setEditing(ev); setDialogOpen(true); };
  const todayStr = ymd(new Date());
  const monthLabel = cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  return (
    <div className="p-6 lg:p-8" data-testid="calendar-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-gray-900 flex items-center gap-2">
            <CalendarDays className="w-7 h-7 text-[#002FA7]" /> Calendar
          </h1>
          <p className="text-sm text-gray-500 mt-1">Microsoft 365 calendars</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={mailbox} onValueChange={setMailbox}>
            <SelectTrigger data-testid="calendar-mailbox-select" className="w-[220px] rounded-sm"><SelectValue placeholder="Select calendar" /></SelectTrigger>
            <SelectContent>
              {calendars.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}{c.type === "shared" ? " (shared)" : ""}</SelectItem>)}
            </SelectContent>
          </Select>
          <button onClick={() => openNew(todayStr)} data-testid="new-event-button"
            className="flex items-center gap-1.5 bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm">
            <Plus className="w-4 h-4" /> New event
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1">
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} data-testid="cal-prev" className="p-2 rounded-sm hover:bg-gray-100 text-gray-500"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={() => { const d = new Date(); setCursor(new Date(d.getFullYear(), d.getMonth(), 1)); }} data-testid="cal-today" className="px-3 py-1.5 rounded-sm hover:bg-gray-100 text-sm font-semibold text-gray-600">Today</button>
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} data-testid="cal-next" className="p-2 rounded-sm hover:bg-gray-100 text-gray-500"><ChevronRight className="w-4 h-4" /></button>
        </div>
        <h2 className="font-heading text-lg font-bold text-gray-900" data-testid="cal-month-label">{monthLabel}</h2>
        <div className="w-24 text-right">{loading && <Loader2 className="w-4 h-4 animate-spin text-[#002FA7] inline" />}</div>
      </div>

      {error && <div className="mb-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2" data-testid="calendar-error">{error}</div>}

      <div className="bg-white border border-gray-200 rounded-sm overflow-hidden shadow-sm">
        <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
          {WEEKDAYS.map((d) => <div key={d} className="px-2 py-2 text-xs font-bold text-gray-400 text-center">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {days.map((d, i) => {
            const ds = ymd(d);
            const inMonth = d.getMonth() === cursor.getMonth();
            const dayEvents = eventsByDay[ds] || [];
            return (
              <div key={ds} onClick={() => openNew(ds)} data-testid={`cal-day-${ds}`}
                className={`min-h-[96px] border-b border-r border-gray-100 p-1.5 cursor-pointer hover:bg-blue-50/40 transition-colors ${inMonth ? "" : "bg-gray-50/60"}`}>
                <div className={`text-xs font-semibold mb-1 ${ds === todayStr ? "bg-[#002FA7] text-white w-5 h-5 rounded-full flex items-center justify-center" : inMonth ? "text-gray-700" : "text-gray-300"}`}>{d.getDate()}</div>
                <div className="space-y-1">
                  {dayEvents.slice(0, 3).map((ev) => (
                    <button key={ev.id} onClick={(e) => { e.stopPropagation(); openEdit(ev); }} data-testid={`cal-event-${ev.id}`}
                      className="w-full text-left text-[11px] bg-[#002FA7]/10 text-[#002FA7] rounded-sm px-1.5 py-0.5 truncate hover:bg-[#002FA7]/20">
                      {!ev.isAllDay && <span className="font-semibold mr-1">{(ev.start?.dateTime || "").slice(11, 16)}</span>}{ev.subject}
                    </button>
                  ))}
                  {dayEvents.length > 3 && <div className="text-[10px] text-gray-400 px-1.5">+{dayEvents.length - 3} more</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <EventFormDialog open={dialogOpen} onOpenChange={setDialogOpen} event={editing} mailbox={mailbox} defaultDate={defaultDate} onSaved={load} />
    </div>
  );
}
